/**
 * The due & overdue list — recurring obligations and ad-hoc jobs together,
 * since from an operational "what needs attention" view the distinction doesn't
 * matter.
 *
 * Began as a rebuild of `eqpractice/webapp.py`'s index route: the same
 * columns, the same overdue split with its counts, the same inline status
 * control, with the filter in the URL so a view can be sent to someone.
 *
 * 2026-10-24 (Wimpie, usability pass): three tabs (due & overdue / overdue /
 * due soon), filters by type, status and client, sortable columns, readable
 * type names, hover help in place of `title`, and a status control that starts
 * blank so "Set" is never one click from a write nobody chose. The view rules
 * live in `../due-list.ts`; every filter and the sort stay in the URL.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError, type DueAndOverdueResponse, type ReportItem } from '../api'
import { AppShell } from '../components/AppShell'
import { StatusBadge } from '../components/StatusBadge'
import { Notice, type NoticeState } from '../components/Notice'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { dueLabel, statusStyle } from '../status'
import type { AuthState, ObligationTypeInUse } from '../api'
import { AllocationLabel } from '../components/AllocationLabel'
import { MultiSelect } from '../components/MultiSelect'
import { Tooltip } from '../components/Tooltip'
import { LogTimeDialog } from '../components/TimeCapture'
import { downloadCsv } from '../csv'
import {
  applyListFilters,
  DEFAULT_SORT,
  dueExplanation,
  hasListFilters,
  nextSort,
  readList,
  readSort,
  readView,
  sortItems,
  statusOptions,
  typeLabel,
  typeOptions,
  type DueView,
  type ListSort,
  type SortKey,
} from '../due-list'

/**
 * How far ahead the list looks. Two years of monthly periods for 415 clients
 * is thousands of rows, so the default is the next 45 days; overdue items are
 * always inside the window. "All" is one click away.
 */
const WITHIN_OPTIONS = [
  { key: '14', label: 'Next 14 days' },
  { key: '45', label: 'Next 45 days' },
  { key: '90', label: 'Next 90 days' },
  { key: 'all', label: 'Everything open' },
] as const
const DEFAULT_WITHIN = '45'

export function DueAndOverduePage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const [params, setParams] = useSearchParams()
  const view = readView(params)
  // The server cuts overdue-only itself; "due soon" is the rest of the same
  // report, so it is cut here and needs no second request.
  const overdueOnly = view === 'overdue'
  const asOf = params.get('asOf') ?? undefined
  const withinDays = params.get('within') ?? DEFAULT_WITHIN
  const allocatedTo = params.get('who') ?? ''
  const dueBefore = params.get('before') ?? ''
  const typesParam = params.get('types')
  const statusParam = params.get('status')
  const query = params.get('q') ?? ''
  const sort = readSort(params)
  const isManager = auth.mode === 'anonymous' || auth.user?.role === 'manager'
  // T&B-1 PR 2: Log time from a row -- client fixed, billing job prefilled
  // when one is linked to the row's obligation type.
  const [loggingFor, setLoggingFor] = useState<ReportItem | null>(null)
  const canLogTime = auth.mode === 'cognito'

  const [data, setData] = useState<DueAndOverdueResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  // Bulk selection (manager only). Cleared on every reload and on every change
  // of filter, so a selection can never silently include rows that are no
  // longer on screen.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkStatus, setBulkStatus] = useState<'completed' | 'not_applicable'>('completed')
  const [bulkReason, setBulkReason] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      setSelected(new Set())
      setData(
        await api.dueAndOverdue({ asOf, overdueOnly, withinDays, allocatedTo, dueBefore: dueBefore || undefined })
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [asOf, overdueOnly, withinDays, allocatedTo, dueBefore])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setSelected(new Set())
  }, [view, typesParam, statusParam, query])

  const filters = useMemo(
    () => ({ view, types: readList(typesParam), statuses: readList(statusParam), query }),
    [view, typesParam, statusParam, query]
  )
  const loaded = data?.items ?? []
  const visible = useMemo(
    () => sortItems(applyListFilters(data?.items ?? [], filters), sort),
    // `sort` is rebuilt every render; its two fields are what matter.
    [data, filters, sort.key, sort.dir]
  )
  const filtering = hasListFilters(filters)

  const selectable = visible.filter((item) => item.itemType === 'obligation')
  const allSelected = selectable.length > 0 && selectable.every((item) => selected.has(item.itemId))
  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  async function applyBulk() {
    const ids = [...selected]
    const label = bulkStatus === 'completed' ? 'completed' : 'not applicable'
    const warning =
      bulkStatus === 'completed'
        ? ' Completed is one-way, and periods still in progress skip the review step (the override is written on each one).'
        : ''
    if (!window.confirm(`Mark ${ids.length} period${ids.length === 1 ? '' : 's'} ${label}?${warning}`)) return
    try {
      const r = await api.bulkSetPeriodStatus(ids, bulkStatus, bulkReason.trim())
      setBulkReason('')
      setNotice({
        tone: r.skipped.length > 0 ? 'warning' : 'success',
        message:
          `${r.changed} period${r.changed === 1 ? '' : 's'} marked ${label}` +
          (r.reviewOverridden > 0 ? ` (${r.reviewOverridden} with the review step overridden)` : '') +
          (r.skipped.length > 0
            ? `; ${r.skipped.length} skipped: ${[...new Set(r.skipped.map((s) => s.reason))].join(' / ')}`
            : '.'),
      })
      await load()
    } catch (e) {
      setNotice({ tone: 'danger', message: e instanceof Error ? e.message : String(e) })
    }
  }

  async function refreshPeriods() {
    try {
      const result = await api.ensurePeriods()
      const undated = result.refusals.length
      setNotice({
        tone: undated > 0 ? 'warning' : 'success',
        message:
          `Periods checked for ${result.years.join(' and ')}: ${result.periodsCreated} created, ` +
          `${result.dueDatesSet} due dates set` +
          (undated > 0
            ? `; ${undated} could not be generated or dated -- usually a missing year end or VAT category, or a due date in a year whose holiday calendar is not sourced yet.`
            : '.'),
      })
      await load()
    } catch (e) {
      setNotice({ tone: 'danger', message: e instanceof Error ? e.message : String(e) })
    }
  }

  /** Exports what is on screen: the same rows, filters and order. */
  function exportCsv() {
    if (!data) return
    downloadCsv(
      `due-and-overdue-${data.asOfDate}.csv`,
      ['Client', 'Item', 'Description', 'Due date', 'Days overdue', 'Status', 'Allocated to'],
      visible.map((item) => [
        item.clientName,
        item.itemCode,
        typeLabel(item.itemDescription),
        item.dueDate,
        String(item.daysOverdue),
        statusStyle(item.status).label,
        item.allocation?.name ?? '',
      ])
    )
  }

  async function changeStatus(item: ReportItem, status: string, reason?: string) {
    try {
      if (item.itemType === 'obligation') {
        await api.setObligationPeriodStatus(item.itemId, status, reason)
      } else {
        await api.setJobStatus(item.itemId, status)
      }
      setNotice({
        tone: 'success',
        message: `${item.clientName} — ${item.itemCode} set to ${statusStyle(status).label.toLowerCase()}.`,
      })
      await load()
    } catch (e) {
      // A refused transition is the database doing its job; the server's
      // message already says exactly what was not allowed.
      setNotice({
        tone: e instanceof ApiError && e.status === 409 ? 'warning' : 'danger',
        message: e instanceof Error ? e.message : String(e),
      })
    }
  }

  const update = (change: (next: URLSearchParams) => void, replace = false) => {
    const next = new URLSearchParams(params)
    change(next)
    setParams(next, { replace })
  }
  const setView = (next: DueView) =>
    update((p) => {
      p.delete('overdue') // the pre-2026-10-24 spelling of show=overdue
      if (next === 'all') p.delete('show')
      else p.set('show', next)
    })
  const setParam = (key: string, value: string, fallback = '') =>
    update((p) => {
      if (value === fallback) p.delete(key)
      else p.set(key, value)
    })
  const setList = (key: string, values: string[]) =>
    update((p) => {
      if (values.length === 0) p.delete(key)
      else p.set(key, values.join(','))
    })
  // Typing in the search must not leave one history entry per keystroke.
  const setQuery = (value: string) =>
    update((p) => {
      if (value === '') p.delete('q')
      else p.set('q', value)
    }, true)
  const setSort = (key: SortKey) => {
    const next: ListSort = nextSort(sort, key)
    update((p) => {
      if (next.key === DEFAULT_SORT.key && next.dir === DEFAULT_SORT.dir) {
        p.delete('sort')
        p.delete('dir')
      } else {
        p.set('sort', next.key)
        p.set('dir', next.dir)
      }
    })
  }
  const clearListFilters = () =>
    update((p) => {
      for (const key of ['types', 'status', 'q', 'overdue', 'show']) p.delete(key)
    })

  const dueSoonCount = data ? data.totalCount - data.overdueCount : null

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Practice"
      title="Due & overdue"
      subtitle={
        data
          ? `Recurring obligations and ad-hoc jobs, as at ${data.asOfDate}. Changing a status here writes straight to the database — there is no undo.`
          : undefined
      }
    >
      {notice && <Notice notice={notice} onDismiss={() => setNotice(null)} />}

      <div className="mb-3 flex flex-wrap items-center gap-2 print:hidden">
        <FilterTab
          active={view === 'all'}
          onClick={() => setView('all')}
          help="Everything still open that is overdue or falls due inside the look-ahead window."
        >
          Due &amp; overdue{data ? ` (${data.totalCount})` : ''}
        </FilterTab>
        <FilterTab
          active={view === 'overdue'}
          onClick={() => setView('overdue')}
          help="Past its due date and not yet completed."
        >
          Overdue{data ? ` (${data.overdueCount})` : ''}
        </FilterTab>
        <FilterTab
          active={view === 'soon'}
          onClick={() => setView('soon')}
          help="Due today or later, inside the look-ahead window."
        >
          Due soon{dueSoonCount !== null ? ` (${dueSoonCount})` : ''}
        </FilterTab>
        <select
          className="eq-input-sm ml-2"
          aria-label="How far ahead"
          value={withinDays}
          onChange={(event) => setParam('within', event.target.value, DEFAULT_WITHIN)}
        >
          {WITHIN_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          className="eq-input-sm"
          aria-label="Allocated to"
          value={allocatedTo}
          onChange={(event) => setParam('who', event.target.value)}
        >
          <option value="">Everyone</option>
          {auth.user && <option value={auth.user.id}>My work</option>}
          <option value="unallocated">Unallocated</option>
          {data?.staff
            .filter((person) => person.id !== auth.user?.id)
            .map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
        </select>
        <label className="flex items-center gap-1.5 text-[12.5px] text-eq-neutral">
          Due before
          <input
            type="date"
            className="eq-input-sm"
            value={dueBefore}
            onChange={(event) => setParam('before', event.target.value)}
            aria-label="Due before"
          />
        </label>
        <span className="ml-auto flex gap-2">
          <Tooltip content="Downloads the rows on screen, with these filters and this order." focusable={false}>
            <button type="button" className="eq-btn-sm border border-eq-border text-eq-neutral hover:bg-eq-ice" onClick={exportCsv}>
              Export CSV
            </button>
          </Tooltip>
          <button type="button" className="eq-btn-sm border border-eq-border text-eq-neutral hover:bg-eq-ice" onClick={() => window.print()}>
            Print
          </button>
          {isManager && (
            <Tooltip
              content="Creates any missing periods for this year and next, and retries due dates that could not be set. Safe to run more than once."
              focusable={false}
            >
              <button
                type="button"
                className="eq-btn-sm border-[1.5px] border-eq-navy text-eq-navy hover:bg-eq-navy hover:text-white"
                onClick={() => void refreshPeriods()}
              >
                Refresh periods
              </button>
            </Tooltip>
          )}
        </span>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <input
          type="search"
          className="eq-input-sm w-56"
          placeholder="Search clients"
          aria-label="Search clients"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <MultiSelect
          label="Type"
          options={typeOptions(loaded)}
          selected={filters.types}
          onChange={(values) => setList('types', values)}
        />
        <MultiSelect
          label="Status"
          options={statusOptions(loaded)}
          selected={filters.statuses}
          onChange={(values) => setList('status', values)}
        />
        {data && (
          <span className="ml-1 text-[12.5px] text-eq-helper">
            {filtering ? `Showing ${visible.length} of ${loaded.length}` : `${loaded.length} shown`}
          </span>
        )}
        {filtering && (
          <button type="button" className="text-[12.5px] text-eq-deep underline" onClick={clearListFilters}>
            Clear filters
          </button>
        )}
      </div>

      {isManager && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-eq-md border border-eq-navy bg-eq-ice px-3 py-2 text-[13px] print:hidden">
          <span className="font-semibold text-eq-deep">
            {selected.size} selected
          </span>
          <select
            className="eq-input-sm"
            value={bulkStatus}
            aria-label="Bulk status"
            onChange={(event) => setBulkStatus(event.target.value as 'completed' | 'not_applicable')}
          >
            <option value="completed">Mark completed</option>
            <option value="not_applicable">Mark not applicable</option>
          </select>
          <input
            className="eq-input-sm min-w-[22rem]"
            placeholder="Reason, recorded on every period (required)"
            aria-label="Reason for the bulk update"
            value={bulkReason}
            onChange={(event) => setBulkReason(event.target.value)}
          />
          <button
            type="button"
            disabled={bulkReason.trim().length < 5}
            className="eq-btn-sm border-[1.5px] border-eq-navy bg-eq-navy text-white disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => void applyBulk()}
          >
            Apply
          </button>
          <button type="button" className="text-[12.5px] underline" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {error && <ErrorState message={error} />}
      {!error && !data && <LoadingState what="the due and overdue list" />}

      {data && loaded.length === 0 && (
        <EmptyState>
          {overdueOnly
            ? 'Nothing is overdue as at this date.'
            : 'Nothing is due or overdue as at this date.'}
        </EmptyState>
      )}

      {data && loaded.length > 0 && visible.length === 0 && (
        <EmptyState>
          Nothing matches these filters.{' '}
          <button type="button" className="text-eq-deep underline" onClick={clearListFilters}>
            Clear filters
          </button>
        </EmptyState>
      )}

      {/* The table scrolls in its own box rather than being clipped by an
          overflow-hidden one: BRAND.md §5 wants a sticky header on a data
          table, and `position: sticky` does nothing when the nearest scroll
          container cannot scroll. With 944 rows in the real dataset, keeping
          the column heads in view is the whole point. */}
      {data && visible.length > 0 && (
        <div className="max-h-[calc(100vh-18rem)] overflow-auto rounded-eq-md border border-eq-border shadow-eq-card">
          <table className="eq-table">
            <thead>
              <tr>
                {isManager && (
                  <th className="w-8 print:hidden">
                    <input
                      type="checkbox"
                      aria-label="Select every obligation shown"
                      checked={allSelected}
                      onChange={() =>
                        setSelected(allSelected ? new Set() : new Set(selectable.map((item) => item.itemId)))
                      }
                    />
                  </th>
                )}
                <SortHeader column="client" sort={sort} onSort={setSort}>
                  Client
                </SortHeader>
                <SortHeader column="item" sort={sort} onSort={setSort}>
                  Item
                </SortHeader>
                <SortHeader column="due" sort={sort} onSort={setSort}>
                  Due date
                </SortHeader>
                <SortHeader column="allocated" sort={sort} onSort={setSort}>
                  Allocated to
                </SortHeader>
                <SortHeader column="status" sort={sort} onSort={setSort}>
                  Status
                </SortHeader>
                <th className="print:hidden">Change status to</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={`${item.itemType}:${item.itemId}`}>
                  {isManager && (
                    <td className="print:hidden">
                      {item.itemType === 'obligation' && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${item.clientName} ${item.itemCode} due ${item.dueDate}`}
                          checked={selected.has(item.itemId)}
                          onChange={() => toggle(item.itemId)}
                        />
                      )}
                    </td>
                  )}
                  <td className="font-medium">{item.clientName}</td>
                  <td>
                    <span className="font-heading text-[13px] font-bold text-eq-navy">
                      {item.itemCode}
                    </span>
                    <span className="text-eq-helper"> — {typeLabel(item.itemDescription)}</span>
                    {item.itemType === 'job' && (
                      <span className="ml-2 rounded-eq-pill bg-eq-ice px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-deep">
                        Job
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap tabular-nums">
                    {item.dueDate}
                    {/* A real separator, not just a margin: without it the two
                        run together as "2026-02-06205 days overdue" for anything
                        reading the text rather than the layout. */}
                    <span className="mx-2 text-eq-border" aria-hidden>
                      ·
                    </span>
                    <Tooltip content={dueExplanation(item.dueDate, item.daysOverdue)} focusable={false}>
                      <span
                        className={
                          item.isOverdue ? 'font-semibold text-eq-danger' : 'text-eq-helper'
                        }
                      >
                        {dueLabel(item.daysOverdue)}
                      </span>
                    </Tooltip>
                  </td>
                  <td>
                    <AllocationLabel allocation={item.allocation} />
                  </td>
                  <td>
                    <StatusBadge status={item.status} kind={item.itemType} />
                  </td>
                  <td className="print:hidden">
                    <StatusControl
                      // Keyed by status: after a change the row's choice starts
                      // blank again rather than carrying the last pick over.
                      key={item.status}
                      nextStatuses={item.nextStatuses}
                      onChange={(status, reason) => void changeStatus(item, status, reason)}
                    />
                    {canLogTime && (
                      <button
                        type="button"
                        className="ml-2 text-[12px] text-eq-deep underline"
                        onClick={() => setLoggingFor(item)}
                        aria-label={`Log time for ${item.clientName} ${item.itemCode}`}
                      >
                        Log time
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {loggingFor && (
        <LogTimeDialog
          client={{ id: loggingFor.clientId, clientNumber: 0, shortName: loggingFor.clientName }}
          obligationTypeCode={loggingFor.itemType === 'obligation' ? loggingFor.itemCode : null}
          narrative={`${loggingFor.itemCode} due ${loggingFor.dueDate}`}
          onClose={() => setLoggingFor(null)}
          onSaved={(e) => {
            setLoggingFor(null)
            setNotice({ tone: 'success', message: `Logged ${e.hours} h on ${e.job.name} for ${loggingFor.clientName} as a draft on your week.` })
          }}
        />
      )}
    </AppShell>
  )
}

function FilterTab({
  active,
  onClick,
  help,
  children,
}: {
  active: boolean
  onClick: () => void
  help: string
  children: ReactNode
}) {
  return (
    <Tooltip content={help} focusable={false}>
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? 'true' : undefined}
        className={[
          'rounded-eq-pill px-3.5 py-1.5 font-heading text-[13px] font-bold',
          active
            ? 'bg-eq-navy text-white'
            : 'border border-eq-border bg-white text-eq-neutral hover:bg-eq-ice',
        ].join(' ')}
      >
        {children}
      </button>
    </Tooltip>
  )
}

/** A column head that sorts: click once ascending, again descending. */
function SortHeader({
  column,
  sort,
  onSort,
  children,
}: {
  column: SortKey
  sort: ListSort
  onSort: (key: SortKey) => void
  children: ReactNode
}) {
  const active = sort.key === column
  return (
    <th aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className={[
          'inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-[.08em] hover:text-eq-navy',
          active ? 'text-eq-navy' : '',
        ].join(' ')}
        onClick={() => onSort(column)}
      >
        {children}
        <span aria-hidden className={active ? '' : 'text-eq-border'}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  )
}

/**
 * A select plus a button rather than a select that fires on change: this writes
 * straight to the database with no undo, so it takes a deliberate second act.
 * The Flask build worked the same way.
 *
 * Since 2026-10-24 the select starts on "Change…", not on the first allowed
 * status, so "Set" stays disabled until someone has actually chosen. Before,
 * every row offered a ready-made "For review" one click from a write.
 */
export function StatusControl({
  nextStatuses,
  onChange,
}: {
  nextStatuses: string[]
  /** `reason` is set only for not_applicable, which requires one (D7). */
  onChange: (status: string, reason?: string) => void
}) {
  const [selected, setSelected] = useState('')
  const [reason, setReason] = useState('')

  if (nextStatuses.length === 0) {
    return (
      <Tooltip content="No status change is available to you from here." focusable={false}>
        <span className="text-[12.5px] text-eq-helper">—</span>
      </Tooltip>
    )
  }

  const chosen = nextStatuses.includes(selected) ? selected : ''
  const needsReason = chosen === 'not_applicable'
  const ready = chosen !== '' && (!needsReason || reason.trim() !== '')

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1.5">
        <select
          className="eq-input-sm"
          value={chosen}
          onChange={(event) => setSelected(event.target.value)}
          aria-label="New status"
        >
          <option value="" disabled>
            Change…
          </option>
          {nextStatuses.map((status) => (
            <option key={status} value={status}>
              {statusStyle(status).label}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!ready}
          className="eq-btn-sm border-[1.5px] border-eq-navy text-eq-navy hover:bg-eq-navy hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => {
            onChange(chosen, needsReason ? reason.trim() : undefined)
            setSelected('')
            setReason('')
          }}
        >
          Set
        </button>
      </div>
      {needsReason && (
        <input
          className="eq-input-sm w-56"
          placeholder="Why is nothing due? (required)"
          aria-label="Reason it is not applicable"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      )}
    </div>
  )
}
